import './AutoRenderDashboard.css';

import React, { useEffect, useMemo, useState } from 'react';
import { Alert, Button, Result, Spin, message } from 'antd';
import { ReloadOutlined } from '@ant-design/icons';

import { autoRenderingFetcher } from '../../services/AutoRenderingFetcher';
import { redoFailedRenderTasks } from '../../services/renders';
import type { StatusReportData, RenderJob } from '../../types/renders';
import type { Project } from '../../types/project';
import type { Counter, ProjectView, UnitNumberView, UnitTypeView, ClosetSnapshot, JobStatus } from './types';
import { counterDefaults, closetCounterDefaults, closetStatusOrder, normalizeClosetStatus, getProjectKey } from './utils';

import { DashboardHeader } from './components/DashboardHeader';
import { SummaryCards } from './components/SummaryCards';
import { ProjectList } from './components/ProjectList';

const AutoRenderDashboard: React.FC = () => {
  const [data, setData] = useState<StatusReportData>();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string>();
  const [refreshFlag, setRefreshFlag] = useState(0);
  const [redoLoading, setRedoLoading] = useState<Record<string, boolean>>({});
  const [fullProjects, setFullProjects] = useState<Record<string, Project>>({});
  const [expandingProject, setExpandingProject] = useState<string | null>(null);

  useEffect(() => {
    let cancelled = false;

    const fetchData = async () => {
      setLoading(true);
      setError(undefined);
      try {
        const response = await autoRenderingFetcher.fetchAllProjects();
        if (!cancelled) {
          setData(response);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load auto render data');
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    void fetchData();

    return () => {
      cancelled = true;
    };
  }, [refreshFlag]);

  const jobSummary = useMemo(() => {
    const byProject = new Map<string, Counter>();
    const byUnitNumber = new Map<string, Counter>();

    const collect = (jobs: RenderJob[], status: JobStatus) => {
      jobs.forEach((job) => {
        if (!job.projectId) {
          return;
        }
        const projectEntry = byProject.get(job.projectId) ?? counterDefaults();
        projectEntry[status] += 1;
        projectEntry.total += 1;
        byProject.set(job.projectId, projectEntry);

        if (job.unitNumberId) {
          const unitEntry = byUnitNumber.get(job.unitNumberId) ?? counterDefaults();
          unitEntry[status] += 1;
          unitEntry.total += 1;
          byUnitNumber.set(job.unitNumberId, unitEntry);
        }
      });
    };

    if (data) {
      collect(data.newRenderJobs ?? [], 'new');
      collect(data.pendingRenderJobs ?? [], 'pending');
      collect(data.failedRenderJobs ?? [], 'failed');
    }

    return { byProject, byUnitNumber };
  }, [data]);

  const projects = useMemo<ProjectView[]>(() => {
    if (!data) {
      return [];
    }

    const { byProject, byUnitNumber } = jobSummary;
    const views: ProjectView[] = [];

    data.allProjects.forEach((renderingProject, index) => {
      const candidateKeys = [renderingProject.id, renderingProject.projectId, renderingProject.pk].filter(
        (value): value is string => Boolean(value),
      );
      const key = getProjectKey(renderingProject, index);
      
      // Check if we have the full project details loaded
      const fullProject = candidateKeys.reduce<Project | undefined>((found, id) => found || fullProjects[id], undefined);

      // Use full project if available, otherwise use rendering project (which has subset of fields)
      // We cast renderingProject to Project because it shares many fields, but we need to be careful
      // about missing fields like unitTypes if not loaded.
      const project = fullProject || (renderingProject as unknown as Project);

      // If we have full project, calculate unit/closet stats from it
      // If not, use the summary quantities from RenderingProject
      let unitTypes: UnitTypeView[] = [];
      const closetCounts = closetCounterDefaults();
      let unitCount = 0;

      if (fullProject) {
        unitTypes = (project.unitTypes ?? []).map((unitType) => {
          const unitNumbers: UnitNumberView[] = (unitType.unitNumbers ?? []).map((unitNumber) => {
            const closets: ClosetSnapshot[] = (unitNumber.closets ?? [])
              .map((closet) => {
                const state = normalizeClosetStatus(closet.renderStatus);
                const imageUrl = closet.s3Urls?.['2D_png'] || closet.image2D;
                return { closet, state, imageUrl: imageUrl || undefined };
              })
              .sort((a, b) => closetStatusOrder[a.state] - closetStatusOrder[b.state]);
  
            const unitJobCounters = unitNumber.id ? byUnitNumber.get(unitNumber.id) : undefined;
            const unitJobs: Counter = unitJobCounters ? { ...unitJobCounters } : counterDefaults();
  
            return { unitNumber, unitJobs, closets };
          });
  
          return { unitType, unitNumbers };
        });

        unitTypes.forEach((typeView) => {
          typeView.unitNumbers.forEach((unitView) => {
            unitView.closets.forEach((closetView) => {
              closetCounts.total += 1;
              switch (closetView.state) {
                case 'done':
                  closetCounts.done += 1;
                  break;
                case 'failed':
                  closetCounts.failed += 1;
                  break;
                case 'pending':
                  closetCounts.pending += 1;
                  break;
                case 'new':
                  closetCounts.new += 1;
                  break;
                default:
                  closetCounts.unknown += 1;
                  break;
              }
            });
          });
        });

        unitCount = project.unitNumbersQty ??
          unitTypes.reduce((total, typeView) => total + typeView.unitNumbers.length, 0);

      } else {
        // Use summary data from RenderingProject
        closetCounts.total = renderingProject.closetsQty ?? 0;
        closetCounts.failed = renderingProject.failedTasksQty ?? 0;
        closetCounts.new = renderingProject.newTasksQty ?? 0;
        closetCounts.pending = renderingProject.pendingTasksQty ?? 0;
        // We don't have exact 'done' count in summary, but we can infer or leave it.
        // Actually, total = done + failed + pending + new roughly.
        // So done = total - failed - pending - new.
        const nonDone = closetCounts.failed + closetCounts.pending + closetCounts.new;
        closetCounts.done = Math.max(0, closetCounts.total - nonDone);
        
        unitCount = renderingProject.unitNumbersQty ?? 0;
      }

      const activeClosets = Math.max(0, closetCounts.total - closetCounts.done);
      const progress =
        closetCounts.total > 0 ? Math.round((closetCounts.done / closetCounts.total) * 100) : 0;

      if (activeClosets > 0 || closetCounts.total > 0) {
        views.push({ project, key, closetCounts, progress, unitCount, unitTypes });
      }
    });

    return views.sort((a, b) => {
      // sort by project last updatedAt descending
      const dateA = new Date(a.project.updatedAt || a.project.createdAt || 0).getTime();
      const dateB = new Date(b.project.updatedAt || b.project.createdAt || 0).getTime();
      return dateB - dateA;
    });
  }, [data, jobSummary, fullProjects]);

  console.log('Project views:', projects);

  const summary = data?.summary;
  const refreshing = loading && Boolean(data);

  const handleRefresh = () => {
    setRefreshFlag((prev) => prev + 1);
  };

  const handleRedoFailedTasks = async (projectId?: string) => {
    if (!projectId) {
      void message.warning('Project id is missing for this action.');
      return;
    }
    setRedoLoading((prev) => ({ ...prev, [projectId]: true }));
    try {
      await redoFailedRenderTasks(projectId);
      message.success('Redo requested for failed render tasks.');
      setRefreshFlag((prev) => prev + 1);
    } catch (err) {
      const description =
        err instanceof Error ? err.message : 'Unable to redo failed render tasks.';
      message.error(description);
    } finally {
      setRedoLoading((prev) => {
        const { [projectId]: _, ...rest } = prev;
        return rest;
      });
    }
  };

  const handleProjectExpand = async (key: string | string[]) => {
    // If key is array (shouldn't be with accordion), take first or ignore
    const projectKey = Array.isArray(key) ? key[0] : key;
    if (!projectKey) return;

    // Find the project ID from the key (key is usually index-id)
    // We need to find the project in our views to get the real ID
    const projectView = projects.find(p => p.key === projectKey);
    if (!projectView) return;

    const projectId = projectView.project.id || projectView.project.projectId || projectView.project.pk;
    if (!projectId) return;

    if (fullProjects[projectId]) {
      return; // Already loaded
    }

    setExpandingProject(projectId);
    try {
      const fullProject = await autoRenderingFetcher.fetchFullProject(projectId);
      setFullProjects(prev => ({ ...prev, [projectId]: fullProject }));
    } catch (err) {
      message.error('Failed to load full project details');
    } finally {
      setExpandingProject(null);
    }
  };

  const handleRefreshProject = async (projectId: string) => {
    setExpandingProject(projectId);
    try {
      const fullProject = await autoRenderingFetcher.fetchFullProject(projectId);
      setFullProjects((prev) => ({ ...prev, [projectId]: fullProject }));
      message.success('Project refreshed');
    } catch (err) {
      message.error('Failed to refresh project');
    } finally {
      setExpandingProject(null);
    }
  };

  if (loading && !data) {
    return (
      <div className='auto-render-dashboard__loading'>
        <Spin size='large' tip='Loading auto render data...' />
      </div>
    );
  }

  if (!loading && error && !data) {
    return (
      <Result
        status='error'
        title='Unable to load auto render dashboard'
        subTitle={error}
        extra={
          <Button type='primary' icon={<ReloadOutlined />} onClick={handleRefresh}>
            Try Again
          </Button>
        }
      />
    );
  }

  return (
    <div className='auto-render-dashboard'>
      <DashboardHeader onRefresh={handleRefresh} loading={refreshing} />

      {error && data && (
        <Alert
          type='error'
          showIcon
          className='auto-render-dashboard__alert'
          message='The dashboard is showing cached data'
          description={error}
          closable
          onClose={() => setError(undefined)}
        />
      )}

      <Spin spinning={refreshing} tip='Refreshing auto render data...'>
        <div className='auto-render-dashboard__content'>
          <SummaryCards
            totalProjects={summary?.totalProjects ?? projects.length}
            newJobs={summary?.newJobs ?? 0}
            pendingJobs={summary?.pendingJobs ?? 0}
            failedJobs={summary?.failedJobs ?? 0}
          />

          <ProjectList
            projects={projects}
            redoLoading={redoLoading}
            onRedoFailedTasks={handleRedoFailedTasks}
            onProjectExpand={handleProjectExpand}
            expandingProject={expandingProject}
            onRefreshProject={handleRefreshProject}
          />
        </div>
      </Spin>
    </div>
  );
};

export default AutoRenderDashboard;

